/**
 * SF-4652: the review page's memory of what it has shown. Each time a new version of the change
 * set arrives, it is compared with the version shown before (the highlight). The chat is sf-api's
 * (`history`, the last good read), then each line the page added itself that the history has not
 * confirmed yet, in the order the page added them. A line is confirmed by its ids, never by its
 * words or its place, so a repeated message, a capped history or a late read cannot hide or double
 * one. Old saved lines with no ids simply show.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DemoBuildMessage, DemoBuildStatus, DemoChangeSet } from './api/demoBuildsClient';
import { describeDiff, diffChangeSets, type ChangeSetDiff } from './changeSetDiff';
import type { ChatLine } from './components/ReviewChat';

/** A line the page added itself, and what in sf-api's chat shows it is saved. */
export type LocalLine = ChatLine &
  (
    | { kind: 'you'; clientId: string }
    | { kind: 'version'; version: number }
    | { kind: 'nochange'; replyTo: string }
  );

export const NO_CHANGE = 'No change to the set this time.';

/** sf-api's chat has this line: the send's client_id, Eva's version, or Eva's reply_to. */
export function confirms(history: DemoBuildMessage[], line: LocalLine): boolean {
  if (line.kind === 'you')
    return history.some((h) => h.from === 'you' && h.client_id === line.clientId);
  if (line.kind === 'version')
    return history.some((h) => h.from === 'eva' && h.version === line.version);
  return history.some((h) => h.from === 'eva' && h.reply_to === line.replyTo);
}

const unconfirmed = (history: DemoBuildMessage[] | null, local: LocalLine[]) =>
  history ? local.filter((l) => !confirms(history, l)) : local;

/** The history, then the page's own lines it has not confirmed, in the page's order. */
export function withHistory(history: DemoBuildMessage[] | null, local: LocalLine[]): ChatLine[] {
  return [...(history ?? []), ...unconfirmed(history, local)].map(({ from, text }) => ({
    from,
    text,
  }));
}

/**
 * `reread` reads sf-api's chat again. It is called once Eva has said more (a new version, or an
 * answer to a message, as drafting ends).
 */
export function useReviewConversation(
  changeSet: DemoChangeSet | null,
  version: number,
  status: DemoBuildStatus | undefined,
  history: DemoBuildMessage[] | null,
  reread: () => unknown,
) {
  const shown = useRef<{ set: DemoChangeSet; version: number } | null>(null);
  const [diff, setDiff] = useState<ChangeSetDiff | null>(null);
  const [lines, setLines] = useState<LocalLine[]>([]);
  // The person's last message (and the version on screen then), until Eva's answer is in.
  const asked = useRef<{ version: number; clientId: string; sawDrafting: boolean } | null>(null);
  const before = useRef<{ status: DemoBuildStatus | undefined; version: number } | null>(null);
  const add = useCallback((line: LocalLine) => setLines((l) => [...l, line]), []);

  useEffect(() => {
    if (!changeSet) return;
    const prev = shown.current;
    if (prev && prev.version === version) return;
    shown.current = { set: changeSet, version };
    if (!prev) {
      if (changeSet.message)
        add({ from: 'eva', text: changeSet.message, kind: 'version', version });
      return;
    }
    const d = diffChangeSets(prev.set, changeSet);
    setDiff(d);
    const said = changeSet.message ?? `Here is version ${version}.`;
    const text = `${said}\n(Version ${version}: ${describeDiff(d)}.)`;
    add({ from: 'eva', text, kind: 'version', version });
    void reread();
  }, [changeSet, version, add, reread]);

  useEffect(() => {
    const prev = before.current;
    before.current = { status, version };
    // Eva answered without a new version (a new one re-reads above).
    const answered = prev?.status === 'drafting' && status !== 'drafting';
    const a = asked.current;
    if (a && status === 'drafting') a.sawDrafting = true;
    else if (a && version > a.version) asked.current = null;
    else if (a?.sawDrafting) {
      asked.current = null;
      if (status === 'ready_for_review')
        add({ from: 'eva', text: NO_CHANGE, kind: 'nochange', replyTo: a.clientId });
    }
    if (answered && version === prev.version) void reread();
  }, [status, version, add, reread]);

  // A line a read has confirmed stays confirmed, even once a capped history no longer holds it.
  // It runs on new lines too: a line added after the history that already holds it is dropped
  // then, not left to come back once a later capped read no longer has it.
  useEffect(() => {
    if (!history) return;
    setLines((ls) => {
      const keep = ls.filter((l) => !confirms(history, l));
      return keep.length === ls.length ? ls : keep;
    });
  }, [history, lines]);

  const said = useCallback(
    (text: string, clientId: string) => {
      asked.current = { version, clientId, sawDrafting: false };
      add({ from: 'you', text, kind: 'you', clientId });
    },
    [version, add],
  );

  // What the latest version changed, said under the chat once sf-api's chat has Eva's line for
  // it; until then the page's own line for that version says it.
  const inline = unconfirmed(history, lines).some(
    (l) => l.kind === 'version' && l.version === diff?.toVersion,
  );
  const versionNote = diff && !inline ? `Version ${diff.toVersion}: ${describeDiff(diff)}.` : null;
  return { diff, lines: withHistory(history, lines), said, versionNote };
}
